import React, { useMemo, useState } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { TOW_VEHICLES, TRAILER_EMPTY_WEIGHT, TYPE_LABELS } from './toolsData';
import css from './ToolsPage.module.css';

const SITE = process.env.REACT_APP_MARKETPLACE_ROOT_URL || 'https://www.bigloads.com';
const CANONICAL = `${SITE}/tools/towing-capacity-calculator`;
const TITLE = 'Towing Capacity Calculator: What Can Your Truck Pull? | BigLoads';
const DESC =
  'Free towing capacity calculator. Enter your truck and trailer to see if you can safely tow it, with a built-in 80% safety margin. Real max tow ratings for popular pickups and SUVs.';

const fmt = n => '$' + Math.round(n).toLocaleString();
const lbs = n => Math.round(n).toLocaleString() + ' lbs';

const FAQS = [
  {
    q: 'How do I find my truck’s towing capacity?',
    a: 'Your maximum tow rating is listed in your owner’s manual and often on a sticker inside the driver’s door jamb. It varies by engine, axle ratio, cab configuration and tow package, so use your specific vehicle’s number. The figures in this calculator are best-case manufacturer maximums for planning only.',
  },
  {
    q: 'What is a safe towing margin?',
    a: 'A common rule of thumb is to stay at or below about 80 percent of your truck’s maximum rated capacity. Towing near the absolute max stresses the drivetrain, brakes and transmission, hurts control, and leaves no cushion for wind, grades or an unevenly loaded trailer. This calculator flags your load against that 80 percent line.',
  },
  {
    q: 'Does towing capacity include the trailer’s weight?',
    a: 'Yes. Your tow rating applies to the combined weight of the trailer plus everything on it (the loaded, or gross, trailer weight). A trailer that weighs 3,000 lbs empty and carries a 4,000 lb load is a 7,000 lb tow. Always plan around the loaded weight, not the empty trailer.',
  },
  {
    q: 'What is tongue weight and why does it matter?',
    a: 'Tongue weight is the downward force the trailer’s coupler puts on your hitch, ideally 10 to 15 percent of the loaded trailer weight for a bumper-pull. Too little causes dangerous trailer sway; too much overloads the rear axle. Proper load balance keeps both tongue weight and handling in the safe zone.',
  },
  {
    q: 'Can I rent a trailer that matches my truck?',
    a: 'Yes. On BigLoads you can filter local listings by trailer type and see the specs before you book, so you can pick a trailer your truck can safely pull. If you are near your limit, choose a lighter or smaller trailer, or a tow vehicle with a higher rating.',
  },
];

const TowingCapacityPageComponent = ({ scrollingDisabled }) => {
  const [vehicle, setVehicle] = useState('ford-f150');
  const [trailerType, setTrailerType] = useState('utility');
  const [emptyWeight, setEmptyWeight] = useState(TRAILER_EMPTY_WEIGHT.utility);
  const [cargoWeight, setCargoWeight] = useState(1500);
  const [customMax, setCustomMax] = useState('');

  const maxTow =
    vehicle === 'other'
      ? Number(customMax) || 0
      : TOW_VEHICLES[vehicle]?.max || 0;

  const onTrailerType = t => {
    setTrailerType(t);
    setEmptyWeight(TRAILER_EMPTY_WEIGHT[t] || 1500);
  };

  const result = useMemo(() => {
    const loaded = Number(emptyWeight) + Number(cargoWeight);
    const safeLimit = maxTow * 0.8;
    const pctOfMax = maxTow > 0 ? (loaded / maxTow) * 100 : 0;
    let status = 'neutral';
    if (maxTow > 0) {
      if (loaded > maxTow) status = 'over';
      else if (loaded > safeLimit) status = 'caution';
      else status = 'ok';
    }
    return { loaded, safeLimit, pctOfMax, status };
  }, [emptyWeight, cargoWeight, maxTow]);

  const articleSchema = {
    '@type': 'Article',
    headline: 'Towing Capacity Calculator: What Can Your Truck Pull?',
    description: DESC,
    author: { '@type': 'Organization', name: 'BigLoads' },
    publisher: { '@type': 'Organization', name: 'BigLoads' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': CANONICAL },
  };
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Tools', item: `${SITE}/tools` },
      { '@type': 'ListItem', position: 3, name: 'Towing Capacity Calculator', item: CANONICAL },
    ],
  };

  const verdictClass =
    result.status === 'ok'
      ? css.verdictOk
      : result.status === 'over'
      ? css.verdictWarn
      : result.status === 'caution'
      ? css.verdictWarn
      : css.verdictNeutral;

  const verdictHead =
    result.status === 'ok'
      ? 'You’re good to tow'
      : result.status === 'caution'
      ? 'Cutting it close'
      : result.status === 'over'
      ? 'Too heavy — do not tow'
      : 'Enter your numbers';

  const verdictBody =
    result.status === 'ok'
      ? `Your loaded trailer (${lbs(result.loaded)}) is under the ~80% safe limit for this vehicle. Good to go, but always confirm your exact rating.`
      : result.status === 'caution'
      ? `Your loaded trailer (${lbs(result.loaded)}) is above the ~80% safe margin (${lbs(result.safeLimit)}) but under the max. Lighten the load or pick a smaller trailer.`
      : result.status === 'over'
      ? `Your loaded trailer (${lbs(result.loaded)}) exceeds this vehicle’s max tow rating (${lbs(maxTow)}). Choose a lighter trailer or a stronger tow vehicle.`
      : 'Pick your vehicle and trailer to see whether you can tow it safely.';

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={TITLE}
      description={DESC}
      schema={[articleSchema, faqSchema, breadcrumbSchema]}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        {/* HERO */}
        <section className={css.hero}>
          <div className={css.wrap}>
            <div className={css.eyebrow}>Free Tool for the Haul</div>
            <h1 className={css.h1}>Towing Capacity Calculator</h1>
            <p className={css.lede}>
              Know what your truck can pull before you book a trailer. Pick your vehicle and load,
              and we’ll check it against a safe towing margin.
            </p>
          </div>
        </section>

        {/* CALCULATOR */}
        <section className={css.calcWrap}>
          <div className={css.wrap}>
            <div className={css.calc}>
              <h2 className={css.calcH}>Can I Tow This?</h2>
              <p className={css.calcSub}>
                Enter your tow vehicle and the trailer you want to pull.
              </p>

              <div className={css.row2}>
                <div className={css.field}>
                  <label className={css.fieldLabel}>Tow Vehicle</label>
                  <select
                    className={css.select}
                    value={vehicle}
                    onChange={e => setVehicle(e.target.value)}
                  >
                    {Object.entries(TOW_VEHICLES).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v.label}
                        {v.max ? ` (up to ${lbs(v.max)})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={css.field}>
                  <label className={css.fieldLabel}>Trailer Type</label>
                  <select
                    className={css.select}
                    value={trailerType}
                    onChange={e => onTrailerType(e.target.value)}
                  >
                    {Object.entries(TYPE_LABELS).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {vehicle === 'other' && (
                <div className={css.field}>
                  <label className={css.fieldLabel}>
                    Your Max Tow Rating <span className={css.fieldHint}>(from your manual / door jamb)</span>
                  </label>
                  <input
                    className={css.input}
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 9500"
                    value={customMax}
                    onChange={e => setCustomMax(e.target.value)}
                  />
                </div>
              )}

              <div className={css.row2}>
                <div className={css.field}>
                  <label className={css.fieldLabel}>
                    Trailer Empty Weight <span className={css.fieldHint}>(lbs)</span>
                  </label>
                  <input
                    className={css.input}
                    type="number"
                    inputMode="numeric"
                    value={emptyWeight}
                    onChange={e => setEmptyWeight(e.target.value)}
                  />
                </div>
                <div className={css.field}>
                  <label className={css.fieldLabel}>
                    Your Cargo Weight <span className={css.fieldHint}>(lbs)</span>
                  </label>
                  <input
                    className={css.input}
                    type="number"
                    inputMode="numeric"
                    value={cargoWeight}
                    onChange={e => setCargoWeight(e.target.value)}
                  />
                </div>
              </div>

              {/* RESULT */}
              <div className={css.result}>
                <div className={`${css.verdict} ${verdictClass}`}>
                  <p className={css.verdictH}>{verdictHead}</p>
                  <p className={css.verdictP}>{verdictBody}</p>
                </div>

                <div className={css.statGrid}>
                  <div className={css.stat}>
                    <div className={css.statNum}>{maxTow ? lbs(maxTow) : '—'}</div>
                    <div className={css.statLabel}>Max tow rating</div>
                  </div>
                  <div className={css.stat}>
                    <div className={css.statNum}>{maxTow ? lbs(result.safeLimit) : '—'}</div>
                    <div className={css.statLabel}>~80% safe limit</div>
                  </div>
                  <div className={css.stat}>
                    <div className={css.statNum}>{lbs(result.loaded)}</div>
                    <div className={css.statLabel}>Your loaded trailer</div>
                  </div>
                </div>

                <div className={css.ctaBand}>
                  <NamedLink className={css.btnOrange} name="SearchPage">
                    Find a Trailer to Rent
                  </NamedLink>
                </div>

                <p className={css.disclaimer}>
                  Max tow ratings shown are best-case manufacturer maximums and vary by trim,
                  engine, axle ratio and tow package. Empty trailer weights are typical ballpark
                  figures; your trailer’s real weight is on its VIN plate. Always verify your
                  vehicle’s rating in your owner’s manual or door-jamb sticker. This tool is
                  for planning only, not a substitute for those figures.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SEO BODY */}
        <section className={css.body}>
          <div className={css.wrap}>
            <h2>How to Use the Towing Capacity Calculator</h2>
            <p>
              Before you rent or haul a trailer, you need to know one thing: can your truck safely
              pull it? This calculator answers that in seconds. Pick your tow vehicle, choose the
              trailer type (we pre-fill a typical empty weight), add the weight of whatever
              you’re hauling, and we compare the total against your vehicle’s max tow rating
              with a built-in safety margin.
            </p>
            <p>
              The number that matters is the <strong>loaded</strong> trailer weight: the empty
              trailer plus your cargo. Your truck’s tow rating applies to that combined figure,
              not just the trailer by itself. If you’re planning a haul, always run the math on
              the fully loaded weight.
            </p>

            <h2>Why the 80% Safe Margin</h2>
            <p>
              Manufacturer tow ratings are absolute maximums measured under ideal conditions. Towing
              right at the limit stresses your transmission, brakes and drivetrain, and leaves no
              cushion for hills, headwinds, or a trailer that’s loaded a little heavy in back.
              Experienced haulers keep their loaded weight at or under roughly 80 percent of the max.
              This tool flags you when you cross that line so you can size down before you’re on
              the highway.
            </p>

            <h2>Don’t Forget Tongue Weight and Balance</h2>
            <p>
              Total weight isn’t the whole story. How you load the trailer matters just as much.
              Aim for tongue weight around 10 to 15 percent of the loaded trailer weight, with about
              60 percent of the cargo ahead of the axle. Get it wrong and you’ll feel it as
              trailer sway. Our{' '}
              <NamedLink name="SingleBlogPage" params={{ title: 'how-to-load-a-trailer-safely' }}>
                guide to loading a trailer safely
              </NamedLink>{' '}
              walks through the 60/40 rule, strapping, and a full pre-trip checklist. You can also
              run the numbers on our{' '}
              <NamedLink name="TrailerPayloadPage">payload calculator</NamedLink>.
            </p>

            <h2>Rent the Right Trailer for Your Truck</h2>
            <p>
              Once you know your safe towing weight, you can pick a trailer that fits. On BigLoads
              you rent directly from local owners and can see each trailer’s specs before you
              book, so you’re never guessing.{' '}
              <NamedLink name="SearchPage">Browse trailer rentals near you</NamedLink> and match the
              trailer to your truck.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className={css.faq}>
          <div className={css.wrap}>
            <h2 className={css.faqH}>Towing Capacity FAQ</h2>
            {FAQS.map(f => (
              <div className={css.faqItem} key={f.q}>
                <p className={css.faqQ}>{f.q}</p>
                <p className={css.faqA}>{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* RELATED TOOLS */}
        <section className={css.related}>
          <div className={css.wrap}>
            <div className={css.relatedGrid}>
              <NamedLink className={css.relatedCard} name="TrailerPayloadPage">
                <div className={css.relatedTitle}>Payload Calculator</div>
                <div className={css.relatedBody}>How much can you load without overweighting?</div>
              </NamedLink>
              <NamedLink className={css.relatedCard} name="SavingsCalculatorPage">
                <div className={css.relatedTitle}>Owner Income Calculator</div>
                <div className={css.relatedBody}>What could your trailer earn on BigLoads?</div>
              </NamedLink>
              <NamedLink
                className={css.relatedCard}
                name="SingleBlogPage"
                params={{ title: 'trailer-rental-cost-guide' }}
              >
                <div className={css.relatedTitle}>Trailer Rental Cost Guide</div>
                <div className={css.relatedBody}>What a fair daily rate looks like by type.</div>
              </NamedLink>
            </div>
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

TowingCapacityPageComponent.propTypes = { scrollingDisabled: bool.isRequired };

const mapStateToProps = state => ({ scrollingDisabled: isScrollingDisabled(state) });

const TowingCapacityPage = compose(connect(mapStateToProps))(TowingCapacityPageComponent);

export default TowingCapacityPage;
